/**
 * 演出效果：粒子（爆炸/火花）、漂浮文字、闪屏、慢动作、屏幕震动转调。
 * 纯逻辑 + 运行时绘制，node 可直接 import。
 */
import { VIEW_W, VIEW_H, MAX_PARTICLES } from '../config.js';
import { Pool } from '../engine/pool.js';
import { randRange, TAU, mulberry32 } from '../engine/math.js';

/** 漂浮文字池容量 */
const MAX_FLOAT_TEXTS = 32;
/** 粒子寿命（秒） */
const PARTICLE_LIFE = 0.6;
/** 漂浮文字寿命（秒） */
const TEXT_LIFE = 1.2;

const PALETTE = ['#fa4', '#f66', '#fc6', '#fff'];

function makeParticle() {
  return { x: 0, y: 0, vx: 0, vy: 0, life: 0, maxLife: 1, color: '#fff', size: 2 };
}

function makeFloatText() {
  return { x: 0, y: 0, str: '', color: '#fff', life: 0 };
}

/**
 * 特效管理：全部为对象池，热循环零分配。
 */
export class Effects {
  constructor() {
    /** @type {Pool} 粒子池 */
    this.particles = new Pool(MAX_PARTICLES, makeParticle);
    /** @type {Pool} 漂浮文字池 */
    this.floatTexts = new Pool(MAX_FLOAT_TEXTS, makeFloatText);
    /** 闪屏不透明度 0..1（每帧衰减） */
    this.flashAlpha = 0;
    /** @type {null|import('../engine/renderer.js').Renderer} 震屏目标（gameScene 注入） */
    this.renderer = null;
    /** 视觉独立 RNG（命中特效等）：与游戏主流 RNG 分流，不扰动录像确定性 */
    this._vfxRng = mulberry32(0x9e3779b9);
  }

  /**
   * 命中火花：几粒短命粒子在命中点迸开（视觉反馈用，不走游戏主流 RNG）。
   * @param {number} x @param {number} y
   * @param {string} [color]
   */
  hitSpark(x, y, color = '#fff', { n = 3, sizeMul = 1, lifeMul = 1 } = {}) {
    for (let i = 0; i < n; i++) {
      const p = this.particles.obtain();
      if (!p) return;
      const a = this._vfxRng() * TAU;
      const sp = 0.6 + this._vfxRng() * 1.4;
      p.x = x;
      p.y = y;
      p.vx = Math.cos(a) * sp;
      p.vy = Math.sin(a) * sp;
      p.life = p.maxLife = (0.18 + this._vfxRng() * 0.12) * lifeMul;
      p.color = color;
      p.size = (1.2 + this._vfxRng() * 0.8) * sizeMul;
    }
  }

  /**
   * 预警虚线：沿 angle 方向铺一串静止虚点，提示"雷将劈向这里"（telegraphed bolt 用）。
   * 粒子无碰撞、位置确定（不用 RNG），只作预警视觉。
   * @param {number} x @param {number} y 起点
   * @param {number} angle 方向（弧度）
   * @param {string} [color]
   */
  telegraphLine(x, y, angle, color = '#ff4') {
    for (let d = 24; d < 260; d += 12) {
      const p = this.particles.obtain();
      if (!p) return;
      p.x = x + Math.cos(angle) * d;
      p.y = y + Math.sin(angle) * d;
      p.vx = 0;
      p.vy = 0;
      p.life = p.maxLife = 0.4;
      p.color = color;
      p.size = 1.5;
    }
  }

  /** 圆形出弹点预告：在 (x,y) 画一圈**静止**虚点，life 秒淡出——
   *  本体是标记"此处将有子弹出现"：位置静态精确、形状=出弹形状，
   *  不做位移/内吸等演出（晃动位置反而看不清"此处"）。 */
  telegraphRing(x, y, radius, color = '#9df', life = 0.6) {
    const n = Math.max(20, Math.floor((TAU * radius) / 14));
    for (let i = 0; i < n; i++) {
      const p = this.particles.obtain();
      if (!p) return;
      const a = (i / n) * TAU;
      p.x = x + Math.cos(a) * radius;
      p.y = y + Math.sin(a) * radius;
      p.vx = 0;
      p.vy = 0;
      p.life = p.maxLife = life;
      p.color = color;
      p.size = 1.8;
    }
  }

  /**
   * 爆炸：一圈放射粒子。
   * @param {number} x @param {number} y
   * @param {number} [scale] 规模倍数（数量/速度/尺寸）
   */
  explosion(x, y, scale = 1) {
    const n = Math.round(16 * scale);
    for (let i = 0; i < n; i++) {
      const p = this.particles.obtain();
      if (!p) return;
      const a = randRange(0, TAU);
      const sp = randRange(0.5, 3) * scale;
      p.x = x;
      p.y = y;
      p.vx = Math.cos(a) * sp;
      p.vy = Math.sin(a) * sp;
      p.life = p.maxLife = PARTICLE_LIFE * randRange(0.6, 1.2);
      p.color = PALETTE[Math.floor(randRange(0, PALETTE.length))];
      p.size = randRange(1.5, 3.5) * scale;
    }
  }

  /**
   * 火花：少量短命粒子（拾取/擦弹反馈）。
   * @param {number} x @param {number} y
   * @param {string} [color]
   */
  sparkle(x, y, color = '#fff') {
    for (let i = 0; i < 4; i++) {
      const p = this.particles.obtain();
      if (!p) return;
      const a = randRange(0, TAU);
      const sp = randRange(0.3, 1.2);
      p.x = x;
      p.y = y;
      p.vx = Math.cos(a) * sp;
      p.vy = Math.sin(a) * sp;
      p.life = p.maxLife = 0.3;
      p.color = color;
      p.size = 1.5;
    }
  }

  /**
   * 漂浮文字（得分反馈）。
   * @param {number} x @param {number} y
   * @param {string} str
   * @param {string} [color]
   */
  floatText(x, y, str, color = '#fff') {
    const t = this.floatTexts.obtain();
    if (!t) return;
    t.x = x;
    t.y = y;
    t.str = str;
    t.color = color;
    t.life = TEXT_LIFE;
  }

  /**
   * 全屏白闪（bomb/boss 击破演出）。
   * @param {number} [alpha] 初始不透明度
   */
  flash(alpha = 0.8) {
    this.flashAlpha = Math.max(this.flashAlpha, alpha);
  }

  /**
   * 慢动作：把 loop.timeScale 调为 scale，frames 帧后恢复 1。
   * @param {import('../engine/loop.js').Loop} loop
   * @param {number} scale 时间缩放
   * @param {number} frames 持续逻辑帧数
   */

  /**
   * 屏幕震动（转调 renderer.shake）。
   * @param {number} frames 持续帧数
   * @param {number} magnitude 最大偏移像素
   */
  shake(frames, magnitude) {
    if (this.renderer) this.renderer.shake(frames, magnitude);
  }

  /**
   * 推进粒子/漂浮文字/闪屏衰减。
   * @param {number} dt 秒
   */
  update(dt) {
    const k = dt * 60;
    this.particles.forEachActive((p) => {
      p.life -= dt;
      p.x += p.vx * k;
      p.y += p.vy * k;
      if (p.life <= 0) this.particles.release(p);
    });
    this.floatTexts.forEachActive((t) => {
      t.life -= dt;
      t.y -= 0.6 * k;
      if (t.life <= 0) this.floatTexts.release(t);
    });
    if (this.flashAlpha > 0) this.flashAlpha = Math.max(0, this.flashAlpha - dt * 3);
  }

  /**
   * 绘制粒子 + 漂浮文字 + 闪屏。
   * @param {import('../engine/renderer.js').Renderer} renderer
   */
  draw(renderer) {
    this.particles.forEachActive((p) => {
      renderer.circle(p.x, p.y, p.size, { fill: p.color, alpha: Math.max(0, p.life / p.maxLife) });
    });
    this.floatTexts.forEachActive((t) => {
      renderer.text(t.str, t.x, t.y, { size: 11, color: t.color, align: 'center', alpha: Math.max(0, t.life / TEXT_LIFE) });
    });
    if (this.flashAlpha > 0) {
      renderer.rect(0, 0, VIEW_W, VIEW_H, { fill: '#fff', alpha: this.flashAlpha });
    }
  }
}
